<template>
  <div class="row bank-account-statement">
    <!-- Bank Account Statement Table -->
    <div class="col-12">
      <div class="card">
        <div
          class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2 py-2"
        >
          <h6 class="mb-0">
            {{ $t("Bank Account Statement") }}
            <span class="badge bg-primary ms-2" v-if="bankAccountName"
              >{{ bankAccountName }}</span
            >
            <span class="badge bg-primary ms-2"
              >{{ pagination?.totalCount || 0 }} {{ $t("transactions") }}</span
            >
          </h6>
          <div class="d-flex gap-2 align-items-center flex-wrap">
            <div class="d-flex align-items-center gap-1">
              <span class="small text-muted">{{ $t("from") }}:</span>
              <div class="input-group input-group-sm" style="width: 150px;">
                <span class="input-group-text py-1"><i class="fas fa-calendar-alt"></i></span>
                <input
                  type="date"
                  class="form-control py-1"
                  v-model="startDate"
                  @change="loadBankAccountStatement()"
                  :title="$t('Show only transactions from this date')"
                />
              </div>
            </div>
            <div class="d-flex align-items-center gap-1">
              <span class="small text-muted">{{ $t("dateTo") }}:</span>
              <div class="input-group input-group-sm" style="width: 150px;">
                <span class="input-group-text py-1"><i class="fas fa-calendar-alt"></i></span>
                <input
                  type="date"
                  class="form-control py-1"
                  v-model="endDate"
                  @change="loadBankAccountStatement()"
                  :title="$t('Show only transactions until this date')"
                />
              </div>
            </div>
            <div class="form-check form-check-inline mb-0 ms-1">
              <input
                class="form-check-input"
                type="checkbox"
                id="bank-statement-hide-empty-date"
                v-model="hideEmptyDate"
                @change="loadBankAccountStatement()"
              />
              <label
                class="form-check-label small text-muted"
                for="bank-statement-hide-empty-date"
                :title="$t('Hide movements that have no date')"
              >
                {{ $t("Hide empty date") }}
              </label>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button
                type="button"
                @click="exportToExcel"
                class="btn btn-success btn-sm"
                :disabled="loading || transactions.length === 0"
                :title="$t('Export to Excel')"
              >
                <i
                  class="fas fa-file-excel me-1"
                  :class="{ 'fa-spin': exportingExcel }"
                ></i>
                {{ $t("Export to Excel") }}
              </button>
              <button
                type="button"
                class="btn btn-sm"
                :class="
                  hiddenStatementColumnCount > 0
                    ? 'btn-secondary'
                    : 'btn-outline-secondary'
                "
                :title="$t('Customize display')"
                :aria-label="$t('Customize display')"
                @click="showColumnSelector = !showColumnSelector"
              >
                <i class="fas fa-columns"></i>
                <template v-if="hiddenStatementColumnCount > 0">
                  {{ displayTableColumns.length }}/{{
                    statementColumnCatalog.length
                  }}</template
                >
              </button>
              <button
                type="button"
                @click="loadBankAccountStatement()"
                class="btn btn-outline-primary btn-sm"
                :disabled="loading"
              >
                <i
                  class="fas fa-sync-alt me-1"
                  :class="{ 'fa-spin': loading }"
                ></i>
                {{ $t("Refresh") }}
              </button>
            </div>
          </div>
        </div>
        <div class="card-body">
          <ColumnSelector
            :show="showColumnSelector"
            :table-fields="statementColumnCatalog"
            :selected-columns="selectedColumns"
            @close="showColumnSelector = false"
            @columns-changed="handleStatementColumnsChanged"
          />

          <div
            v-if="activeStatementFilterChips.length"
            class="d-flex flex-wrap align-items-center gap-2 mb-3"
          >
            <span class="small text-muted">
              <i class="fas fa-filter me-1"></i>{{ $t("Filters") }}:
            </span>
            <span
              v-for="chip in activeStatementFilterChips"
              :key="`bas-chip-${chip.key}`"
              class="badge bg-light text-dark border d-inline-flex align-items-center gap-1"
            >
              <span>{{ $t(chip.label) }}: {{ chip.text }}</span>
              <button
                type="button"
                class="btn btn-link btn-sm p-0 lh-1 text-danger"
                :title="$t('Clear filter')"
                :aria-label="$t('Clear filter')"
                @click="clearStatementColumnFilter(chip.key)"
              >
                <i class="fas fa-times" style="font-size: 0.7rem"></i>
              </button>
            </span>
            <button
              v-if="activeStatementFilterChips.length > 1"
              type="button"
              class="btn btn-link btn-sm p-0 text-muted"
              @click="clearAllStatementColumnFilters"
            >
              {{ $t("Clear all") }}
            </button>
          </div>

          <ContentSkeleton v-if="loading" variant="table" :rows="8" :cols="6" />
          <div v-else-if="transactions.length === 0" class="text-center py-4">
            <i class="fas fa-university fa-3x text-muted mb-3"></i>
            <p class="text-muted">
              {{
                activeStatementFilterChips.length
                  ? $t("No transactions found matching your search")
                  : $t("No transactions found for this bank account")
              }}
            </p>
          </div>
          <div v-else>
            <!-- Summary Cards (Initial Balance is shown as first row in table below) -->
            <div class="row mb-4">
              <div class="col-md-4">
                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="card-title">{{ $t("Total Debits") }}</h6>
                    <h4 class="mb-0 text-danger">
                      -{{ formatAmountWithCurrency(totals.totalPayments, displayCurrency) }}
                    </h4>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="card-title">{{ $t("Total Credits") }}</h6>
                    <h4 class="mb-0 text-success">
                      +{{ formatAmountWithCurrency(totals.totalCollects, displayCurrency) }}
                    </h4>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="card-title">{{ $t("Net Balance") }}</h6>
                    <h4
                      class="mb-0"
                      :class="
                        totals.netBalance >= 0 ? 'text-success' : 'text-danger'
                      "
                    >
                      {{ formatBalanceWithSign(totals.netBalance, displayCurrency) }}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            <div class="table-responsive bas-table-scroll">
              <table class="table table-hover bank-statement-table">
                <colgroup>
                  <col
                    v-for="col in displayTableColumns"
                    :key="`bas-colgroup-${col.key}`"
                    :class="statementColClass(col.key)"
                    :style="listTableColStyle(col.key)"
                  />
                  <col class="bas-col-actions" />
                </colgroup>
                <thead>
                  <tr class="text-center">
                    <th
                      v-for="(col, colIndex) in displayTableColumns"
                      :key="`bas-th-${col.key}`"
                      class="bas-th-draggable"
                      :class="{
                        'sorted-asc':
                          statementSortColumn === col.key &&
                          statementSortDirection === 'asc',
                        'sorted-desc':
                          statementSortColumn === col.key &&
                          statementSortDirection === 'desc',
                        'list-col-pinned': isColumnPinned(col.key),
                        'list-col-pinned-boundary': isDataColPinnedBoundary(
                          col,
                          colIndex
                        ),
                      }"
                      :style="listDataColHeaderStickyStyle(col, colIndex)"
                      :draggable="!isResizing"
                      @dragstart="onDragStart($event, colIndex)"
                      @dragover="onDragOver($event)"
                      @drop="onDrop($event, colIndex)"
                      @dragenter="onDragEnter($event)"
                      @dragleave="onDragLeave($event)"
                    >
                      <div
                        class="d-inline-flex align-items-center justify-content-center gap-1 flex-nowrap"
                      >
                        <button
                          type="button"
                          class="btn btn-link btn-sm py-0 px-1 column-header-toolbar-pin-btn"
                          :class="{
                            'column-header-toolbar-pin-btn--active':
                              isColumnPinned(col.key),
                          }"
                          :title="
                            isColumnPinned(col.key)
                              ? $t('UnpinColumn')
                              : $t('PinColumn')
                          "
                          :aria-label="
                            isColumnPinned(col.key)
                              ? $t('UnpinColumn')
                              : $t('PinColumn')
                          "
                          @click.stop="togglePinColumn(col.key)"
                        >
                          <i class="fas fa-thumbtack" style="font-size: 0.8rem"></i>
                        </button>
                        <i
                          v-if="isStatementColumnSortable(col)"
                          class="cursor-pointer column-header-sort-icon"
                          :class="
                            statementSortColumn === col.key
                              ? statementSortDirection === 'asc'
                                ? 'fas fa-sort-up'
                                : 'fas fa-sort-down'
                              : 'fas fa-sort'
                          "
                          :title="$t('Sort')"
                          @click.stop="toggleStatementSort(col)"
                        ></i>
                        <span>{{ $t(statementColumnLabel(col)) }}</span>
                        <button
                          v-if="isStatementColumnFilterable(col)"
                          type="button"
                          class="btn btn-link btn-sm py-0 px-1 column-header-toolbar-filter-btn"
                          :class="{
                            'column-header-toolbar-filter-btn--active':
                              isStatementColumnFilterActive(col.key),
                          }"
                          :title="$t('Filters')"
                          :aria-label="$t('Filters')"
                          @click.stop="openStatementColumnFilter(col.key)"
                        >
                          <i class="fas fa-filter" style="font-size: 0.8rem"></i>
                        </button>
                      </div>
                      <div
                        class="bas-resize-handle"
                        :title="$t('ResizeColumn')"
                        @mousedown.stop="startResize($event, col.key)"
                        @click.stop
                      ></div>
                    </th>
                    <th class="text-center">{{ $t("Actions") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(transaction, index) in transactions"
                    :key="`${transaction._id}-${index}`"
                  >
                    <td
                      v-for="(col, colIndex) in displayTableColumns"
                      :key="`bas-td-${col.key}`"
                      :class="statementCellClass(col, colIndex)"
                      :style="listDataColStickyStyle(col, colIndex)"
                    >
                      <template v-if="col.key === 'date'">
                        <span class="text-muted">
                          {{ formatDate(transaction.date) }}
                        </span>
                      </template>
                      <template v-else-if="col.key === 'type'">
                        <span
                          class="badge"
                          :class="getTransactionTypeBadgeClass(transaction.type)"
                        >
                          {{ getTransactionTypeLabel(transaction.type) }}
                        </span>
                      </template>
                      <template v-else-if="col.key === 'description'">
                        <div class="d-flex flex-column">
                          <span
                            class="fw-medium fw-bold text-primary cursor-pointer"
                            @click.stop="viewTransaction(transaction)"
                            :title="$t('View transaction details')"
                          >
                            {{ transaction.description || $t("-") }}
                          </span>
                          <small class="text-muted">{{
                            transaction.reference || $t("")
                          }}</small>
                        </div>
                      </template>
                      <template v-else-if="col.key === 'transitDays'">
                        {{ formatStatementTransitDays(transaction) }}
                      </template>
                      <template v-else-if="col.key === 'contact'">
                        <div
                          v-if="transaction.contact"
                          class="d-flex align-items-center"
                        >
                          <i class="fas fa-user me-2 text-muted"></i>
                          <span
                            class="text-primary fw-bold cursor-pointer"
                            @click.stop="viewContact(transaction.contactId)"
                            :title="$t('View contact details')"
                          >
                            {{ transaction.contact }}
                          </span>
                        </div>
                        <span v-else class="text-muted">{{ $t("-") }}</span>
                      </template>
                      <template v-else-if="col.key === 'debit'">
                        <span
                          v-if="transaction.type === 'payment'"
                          class="fw-bold text-danger"
                        >
                          {{ formatAmountOnly(transaction.amount) }}
                        </span>
                        <span v-else class="text-muted">{{ $t("-") }}</span>
                      </template>
                      <template v-else-if="col.key === 'credit'">
                        <span
                          v-if="transaction.type === 'collect'"
                          class="fw-bold text-success"
                        >
                          {{ formatAmountOnly(transaction.amount) }}
                        </span>
                        <span v-else class="text-muted">{{ $t("-") }}</span>
                      </template>
                      <template v-else-if="col.key === 'balance'">
                        <span
                          class="fw-bold"
                          :class="getBalanceClass(getDisplayBalance(transaction))"
                        >
                          {{ formatAmountOnly(Math.abs(getDisplayBalance(transaction))) }}
                        </span>
                      </template>
                      <template v-else-if="col.key === 'comment'">
                        <span
                          v-if="transaction.comment"
                          class="text-muted"
                          :title="transaction.comment"
                        >
                          {{ truncateText(transaction.comment, 30) }}
                        </span>
                        <span v-else class="text-muted">{{ $t("-") }}</span>
                      </template>
                      <template v-else-if="col.key === 'attachedFile'">
                        <button
                          v-if="transaction.attachedFile"
                          @click.stop="viewAttachedFile(transaction.attachedFile)"
                          class="btn btn-outline-primary btn-sm"
                          :title="$t('View attached file')"
                        >
                          <i class="fas fa-paperclip"></i>
                        </button>
                        <span v-else class="text-muted">{{ $t("-") }}</span>
                      </template>
                    </td>
                    <td class="text-center">
                      <div class="btn-group btn-group-sm" role="group">
                        <button
                          @click.stop="viewTransaction(transaction)"
                          class="btn btn-outline-primary btn-sm"
                          :title="$t('View transaction details')"
                        >
                          <i class="fas fa-eye"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <!-- Initial Balance: last row (inicio de los cálculos, de atrás a adelante) -->
                  <tr
                    v-if="hasInitialBalanceRow"
                    class="bg-info bg-opacity-10 text-center fw-bold"
                  >
                    <td
                      v-for="(col, colIndex) in displayTableColumns"
                      :key="`bas-ib-${col.key}`"
                      :class="statementInitialBalanceCellClass(col, colIndex)"
                      :style="listDataColStickyStyle(col, colIndex)"
                    >
                      <template v-if="col.key === 'type'">
                        <span class="badge bg-info">{{ $t("Initial Balance") }}</span>
                      </template>
                      <template v-else-if="col.key === 'debit'">
                        <span v-if="initialBalance < 0" class="fw-bold text-danger">
                          {{ formatAmountOnly(Math.abs(initialBalance)) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </template>
                      <template v-else-if="col.key === 'credit'">
                        <span v-if="initialBalance >= 0" class="fw-bold text-success">
                          {{ formatAmountOnly(initialBalance) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </template>
                      <template v-else-if="col.key === 'balance'">
                        <span
                          class="badge"
                          :class="initialBalance >= 0 ? 'bg-success' : 'bg-danger'"
                        >
                          {{ formatAmountOnly(Math.abs(initialBalance)) }}
                        </span>
                      </template>
                      <template v-else>
                        <span class="text-muted">-</span>
                      </template>
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Load More -->
          <div
            v-if="pagination && pagination.hasNextPage"
            class="row mt-4"
          >
            <div class="col-12 text-center">
              <div class="text-muted small mb-2">
                {{ $t("Showing") }} {{ transactions.length }} {{ $t("of") }}
                {{ pagination.totalCount }} {{ $t("transactions") }}
              </div>
              <button
                class="btn btn-primary"
                @click="loadMore"
                :disabled="loadingMore"
              >
                <span v-if="loadingMore">
                  <i class="fas fa-spinner fa-spin me-2"></i>
                  {{ $t("Loading...") }}
                </span>
                <span v-else>
                  <i class="fas fa-arrow-down me-2"></i>
                  {{ $t("Load More") }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <AccountStatementColumnFilterPopup
      :open="columnFilterPopupOpen"
      :field="columnFilterPopupField"
      :initial-filter="columnFilterPopupInitial"
      :list-options="columnFilterPopupListOptions"
      @close="closeStatementColumnFilter"
      @apply="applyStatementColumnFilter"
    />
  </div>
</template>

<script>
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { wencesApi } from "@/api/wencesApi";
import Swal from "sweetalert2";
import { formatNumberSync } from "@/utils/numberFormatter";
import { formatDateSync } from "@/utils/dateFormatter";
import ExportService from "@/services/exportService";
import AccountStatementColumnFilterPopup from "@/components/financial/AccountStatementColumnFilterPopup.vue";
import ColumnSelector from "@/components/gotrading/common/ColumnSelector.vue";
import columnPreferencesMixin, {
  DEFAULT_COLUMN_WIDTH_PX,
} from "@/components/gotrading/mixins/columnPreferencesMixin";
import {
  isFilterSpecActive,
  serializeActiveColumnFilters as serializeColumnFilterSpecs,
} from "@/utils/columnFilterSpec";

/**
 * Columnas del extracto bancario: catálogo para el ColumnSelector y espejo de
 * `BANK_STATEMENT_SORTABLE_COLUMNS` / `getColumnCell` en
 * `back/modules/financial_module/utils/bankAccountStatementColumnFilters.js`.
 */
const BANK_STATEMENT_COLUMNS = [
  { key: "date", label: "Date", type: "date", filterable: true },
  { key: "type", label: "Type", type: "list", filterable: true },
  { key: "description", label: "Description", type: "string", filterable: true },
  { key: "transitDays", label: "Transit days", type: "number", filterable: true },
  { key: "contact", label: "Contact", type: "string", filterable: true },
  { key: "debit", label: "Debit", type: "number", filterable: true },
  { key: "credit", label: "Credit", type: "number", filterable: true },
  { key: "balance", label: "Balance", type: "number", filterable: true },
  { key: "comment", label: "Comment", type: "string", filterable: true },
  { key: "attachedFile", label: "Attached File", type: "string", filterable: true },
];

/** Clase de ancho por columna (definidas en el <style> de este componente). */
const BANK_STATEMENT_COL_CLASS = {
  date: "bas-col-date",
  type: "bas-col-type",
  description: "bas-col-description",
  transitDays: "bas-col-transit",
  contact: "bas-col-contact",
  debit: "bas-col-money",
  credit: "bas-col-money",
  balance: "bas-col-money",
  comment: "bas-col-comment",
  attachedFile: "bas-col-file",
};

/**
 * Ancho real de cada columna, en px. Espejo de las reglas `.bas-col-*` del
 * <style>: el mixin necesita el ancho para calcular el desplazamiento de las
 * columnas fijadas, y si no coincide con el que pinta el CSS se solapan.
 */
const BANK_STATEMENT_COLUMN_WIDTH_PX = {
  date: 105,
  type: 130,
  description: 220,
  transitDays: 90,
  contact: 170,
  debit: 115,
  credit: 115,
  balance: 115,
  comment: 165,
  attachedFile: 115,
};

/** Clases de celda por columna en las filas de movimiento. */
const BANK_STATEMENT_CELL_CLASS = {
  transitDays: "text-center",
  debit: "text-center",
  credit: "text-center",
  balance: "text-center",
  comment: "text-center",
  attachedFile: "text-center",
};

export default {
  name: "BankAccountStatement",
  components: {
    ContentSkeleton,
    AccountStatementColumnFilterPopup,
    ColumnSelector,
  },
  mixins: [columnPreferencesMixin],
  props: {
    bankAccountId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // Data
      transactions: [],
      bankAccountName: "",
      bankAccountCurrency: "",
      initialBalance: 0,
      pagination: {},
      totals: {
        totalPayments: 0,
        totalCollects: 0,
        initialBalance: 0,
        netBalance: 0,
      },

      // Loading
      loading: false,
      loadingMore: false,
      exportingExcel: false,

      // Pagination
      currentPage: 1,
      itemsPerPage: 20,

      // Filter: only transactions from this date (default 1 March 2026)
      startDate: "2026-03-01",
      // Filter: only transactions until this date (optional; empty = no limit)
      endDate: "",
      // Movements with no date are shown by default; checking this drops them
      hideEmptyDate: false,

      // Columnas y filtros, igual que en el extracto de la ficha de contacto
      showColumnSelector: false,
      columnFilters: {},
      columnFilterPopupOpen: false,
      columnFilterPopupKey: "",
      statementSortColumn: "",
      statementSortDirection: "asc",
    };
  },
  computed: {
    /**
     * Seam del mixin: las preferencias de este extracto se guardan bajo su
     * propia clave (viewId en UserColumnPreference es texto libre).
     */
    columnPreferencesViewId() {
      return "bank_account_statement";
    },
    /** Catálogo completo; hace de `tableFields` para el ColumnSelector. */
    statementColumnCatalog() {
      return BANK_STATEMENT_COLUMNS;
    },
    statementFilterableColumns() {
      return BANK_STATEMENT_COLUMNS.filter((col) => col.filterable);
    },
    /** Espejo de `BANK_STATEMENT_SORTABLE_COLUMNS` en el backend. */
    statementSortableColumns() {
      return BANK_STATEMENT_COLUMNS.map((col) => col.key);
    },
    /** ¿Hay columnas ocultas respecto al catálogo completo? */
    hiddenStatementColumnCount() {
      return BANK_STATEMENT_COLUMNS.length - this.displayTableColumns.length;
    },
    columnFilterPopupField() {
      if (!this.columnFilterPopupKey) return null;
      const col = this.statementFilterableColumns.find(
        (c) => c.key === this.columnFilterPopupKey
      );
      if (!col) return null;
      return { key: col.key, label: col.label, type: col.type };
    },
    columnFilterPopupInitial() {
      if (!this.columnFilterPopupKey) return null;
      return this.columnFilters[this.columnFilterPopupKey] || null;
    },
    /** La única columna de lista es el tipo de movimiento. */
    columnFilterPopupListOptions() {
      if (this.columnFilterPopupKey !== "type") return [];
      return this.statementTypeFilterOptions;
    },
    statementTypeFilterOptions() {
      return [
        { _id: "payment", label: this.$t("Payment") },
        { _id: "collect", label: this.$t("Collection") },
      ];
    },
    /** Filtros activos, para mostrarlos y poder quitarlos de uno en uno. */
    activeStatementFilterChips() {
      return Object.keys(this.columnFilters || {})
        .filter((key) => this.isStatementColumnFilterActive(key))
        .map((key) => {
          const col = this.statementFilterableColumns.find((c) => c.key === key);
          return {
            key,
            label: (col && col.label) || key,
            text: this.statementFilterChipText(key),
          };
        });
    },
    hasInitialBalanceRow() {
      return (
        this.initialBalance !== 0 &&
        this.initialBalance !== null &&
        this.initialBalance !== undefined
      );
    },
    /** Bank account currency (authoritative for statement totals); then first tx; fallback USD */
    displayCurrency() {
      if (this.bankAccountCurrency) return this.bankAccountCurrency;
      const first = (this.transactions || []).find(
        t => t && (t.type === 'payment' || t.type === 'collect') && t.currency
      );
      return (first && first.currency) || 'USD';
    },
  },
  created() {
    // Semilla sincrónica con el catálogo completo: el watcher de bankAccountId
    // corre antes de mounted y la tabla se pintaría sin columnas.
    this.selectedColumns = BANK_STATEMENT_COLUMNS.reduce((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {});
    this.visibleTableFields = [...BANK_STATEMENT_COLUMNS];
  },
  async mounted() {
    // La carga del extracto la dispara el watcher `immediate` de bankAccountId.
    await this.initStatementColumnPreferences();
  },

  watch: {
    bankAccountId: {
      handler(newId) {
        if (newId) {
          this.currentPage = 1;
          this.columnFilters = {};
          this.closeStatementColumnFilter();
          this.statementSortColumn = "";
          this.statementSortDirection = "asc";
          this.loadBankAccountStatement();
        }
      },
      immediate: true,
    },
  },
  methods: {
    // ------------------------------------------------------------- columnas

    /** Seam del mixin: el reorden se guarda solo, igual que las fijadas. */
    onColumnOrderChanged() {
      this.saveStatementColumnPreferences();
    },

    /** Seam del mixin: el ancho también se guarda solo al soltar. */
    onColumnWidthChanged() {
      this.saveStatementColumnPreferences();
    },

    /**
     * Seam del mixin: sin ancho guardado vale el del CSS. Si no coincidieran,
     * las columnas fijadas se solaparían.
     */
    getColumnWidth(fieldKey) {
      return (
        this.columnWidths[fieldKey] ||
        BANK_STATEMENT_COLUMN_WIDTH_PX[fieldKey] ||
        DEFAULT_COLUMN_WIDTH_PX
      );
    },

    statementColClass(colKey) {
      return BANK_STATEMENT_COL_CLASS[colKey] || "";
    },

    statementColumnLabel(col) {
      return (col && col.field && col.field.label) || (col && col.key) || "";
    },

    isStatementColumnFilterable(col) {
      return !!(col && col.field && col.field.filterable);
    },

    /** Clases de columna fijada, comunes a las dos variantes de fila. */
    statementPinnedCellClass(col, colIndex) {
      if (!this.isColumnPinned(col.key)) return "";
      return this.isDataColPinnedBoundary(col, colIndex)
        ? "list-col-pinned list-col-pinned-boundary"
        : "list-col-pinned";
    },

    statementCellClass(col, colIndex) {
      return [
        BANK_STATEMENT_CELL_CLASS[col.key] || "",
        this.statementPinnedCellClass(col, colIndex),
      ];
    },

    /** La fila de saldo inicial ya viene centrada por el <tr>. */
    statementInitialBalanceCellClass(col, colIndex) {
      return this.statementPinnedCellClass(col, colIndex);
    },

    /** Orden guardado primero; lo nuevo del catálogo se agrega al final. */
    buildStatementVisibleFields() {
      const prefs = this.selectedColumns || {};
      const byKey = new Map(BANK_STATEMENT_COLUMNS.map((col) => [col.key, col]));
      const ordered = [];
      const used = new Set();
      (this.savedColumnOrder || []).forEach((key) => {
        if (used.has(key) || !byKey.has(key) || !prefs[key]) return;
        ordered.push(byKey.get(key));
        used.add(key);
      });
      BANK_STATEMENT_COLUMNS.forEach((col) => {
        if (!used.has(col.key) && prefs[col.key]) ordered.push(col);
      });
      return ordered;
    },

    async initStatementColumnPreferences() {
      const preferences = await this.loadColumnPreferences();
      const merged = { ...preferences };
      // Sin preferencia guardada la columna se ve: un usuario nuevo ve el
      // extracto completo, igual que antes de que esto fuera configurable.
      BANK_STATEMENT_COLUMNS.forEach((col) => {
        if (merged[col.key] === undefined) merged[col.key] = true;
      });
      this.selectedColumns = merged;
      this.visibleTableFields = this.buildStatementVisibleFields();
    },

    /** El ColumnSelector mutó `selectedColumns`; se reproyecta el orden. */
    handleStatementColumnsChanged() {
      const previousOrder = this.displayTableColumns.map((col) => col.key);
      this.savedColumnOrder = previousOrder.concat(
        (this.savedColumnOrder || []).filter(
          (key) => !previousOrder.includes(key)
        )
      );
      this.visibleTableFields = this.buildStatementVisibleFields();
      this.saveStatementColumnPreferences();
    },

    async saveStatementColumnPreferences() {
      try {
        await this.saveColumnPreferences(true, false);
        this.savedSelectedColumns = JSON.parse(
          JSON.stringify(this.selectedColumns)
        );
      } catch (error) {
        console.error("Error saving column preferences:", error);
      }
    },

    // --------------------------------------------------------- orden y filtros

    /** Parámetros de orden y filtros por columna que entiende el backend. */
    statementQueryParams() {
      const params = {};
      const activeColumnFilters = serializeColumnFilterSpecs(this.columnFilters);
      if (activeColumnFilters) {
        params.columnFilters = JSON.stringify(activeColumnFilters);
      }
      if (this.statementSortColumn) {
        params.sortColumn = this.statementSortColumn;
        params.sortDirection = this.statementSortDirection;
      }
      return params;
    },

    /** ¿La columna se puede ordenar? Espejo de la lista del backend. */
    isStatementColumnSortable(col) {
      return !!col && this.statementSortableColumns.includes(col.key);
    },

    /**
     * Ciclo del orden en la cabecera: asc → desc → sin orden (vuelve al orden
     * cronológico).
     */
    async toggleStatementSort(col) {
      if (!this.isStatementColumnSortable(col)) return;
      const key = col.key;
      if (this.statementSortColumn !== key) {
        this.statementSortColumn = key;
        this.statementSortDirection = "asc";
      } else if (this.statementSortDirection === "asc") {
        this.statementSortDirection = "desc";
      } else {
        this.statementSortColumn = "";
        this.statementSortDirection = "asc";
      }
      await this.loadBankAccountStatement();
    },

    isStatementColumnFilterActive(columnKey) {
      return isFilterSpecActive(
        this.columnFilters && this.columnFilters[columnKey]
      );
    },

    openStatementColumnFilter(columnKey) {
      const col = this.statementFilterableColumns.find((c) => c.key === columnKey);
      if (!col) return;
      this.columnFilterPopupKey = columnKey;
      this.columnFilterPopupOpen = true;
    },

    closeStatementColumnFilter() {
      this.columnFilterPopupOpen = false;
      this.columnFilterPopupKey = "";
    },

    async applyStatementColumnFilter(payload) {
      const key = this.columnFilterPopupKey;
      if (!key || !payload) {
        this.closeStatementColumnFilter();
        return;
      }
      const next = payload.spec || {};
      if (payload.active) {
        this.$set(this.columnFilters, key, {
          value: next.value,
          operator: next.operator || "=",
          start: next.start || "",
          end: next.end || "",
        });
      } else if (Object.prototype.hasOwnProperty.call(this.columnFilters, key)) {
        this.$delete(this.columnFilters, key);
      }

      this.closeStatementColumnFilter();
      await this.loadBankAccountStatement();
    },

    /** Texto legible del filtro para el chip. */
    statementFilterChipText(columnKey) {
      const spec = (this.columnFilters || {})[columnKey] || {};
      const operator = spec.operator || "=";
      if (operator === "is_null") return this.$t("Is empty");
      if (operator === "is_not_null") return this.$t("Is not empty");
      if (operator === "between") return `${spec.start} → ${spec.end}`;

      const raw =
        spec.value !== null && spec.value !== undefined && spec.value !== ""
          ? spec.value
          : spec.start || spec.end || "";
      const text = Array.isArray(raw)
        ? raw
            .map((item) => this.statementFilterValueLabel(columnKey, item))
            .join(", ")
        : this.statementFilterValueLabel(columnKey, raw);
      return operator === "=" ? text : `${operator} ${text}`;
    },

    statementFilterValueLabel(columnKey, value) {
      const id =
        value && typeof value === "object" ? value._id || value.label : value;
      if (columnKey === "type") {
        const option = this.statementTypeFilterOptions.find(
          (opt) => opt._id === id
        );
        if (option) return option.label;
      }
      return id == null ? "" : String(id);
    },

    async clearStatementColumnFilter(columnKey) {
      if (!Object.prototype.hasOwnProperty.call(this.columnFilters, columnKey)) {
        return;
      }
      this.$delete(this.columnFilters, columnKey);
      await this.loadBankAccountStatement();
    },

    async clearAllStatementColumnFilters() {
      this.columnFilters = {};
      await this.loadBankAccountStatement();
    },

    async loadBankAccountStatement(append = false) {
      if (!this.bankAccountId) return;

      // Solo "load more" concatena: cualquier otro origen (refresh, filtros)
      // reemplaza la lista para no duplicar filas
      append = append === true;

      try {
        if (append) {
          this.loadingMore = true;
        } else {
          this.loading = true;
          this.currentPage = 1;
        }

        const params = {
          page: this.currentPage,
          limit: this.itemsPerPage,
          sortBy: "date",
          sortOrder: "desc",
          startDate: this.startDate || "2026-03-01",
          endDate: this.endDate || undefined,
          hideEmptyDate: this.hideEmptyDate ? true : undefined,
          ...this.statementQueryParams(),
        };

        const response = await wencesApi.get(
          `/api/financial/bank-account-statement/${this.bankAccountId}`,
          { params }
        );

        if (response.data && response.data.success) {
          const newTransactions = response.data.data.transactions || [];

          if (append) {
            this.transactions = [...this.transactions, ...newTransactions];
          } else {
            this.transactions = newTransactions;
          }

          this.bankAccountName = response.data.data.bankAccountName || "";
          this.bankAccountCurrency = response.data.data.bankAccountCurrency || "";
          this.initialBalance = response.data.data.initialBalance || 0;
          this.pagination = response.data.data.pagination || {};
          this.totals = response.data.data.totals || {
            totalPayments: 0,
            totalCollects: 0,
            initialBalance: 0,
            netBalance: 0,
          };
        } else {
          if (!append) {
            this.transactions = [];
            this.bankAccountName = "";
            this.bankAccountCurrency = "";
            this.initialBalance = 0;
            this.pagination = {};
            this.totals = {
              totalPayments: 0,
              totalCollects: 0,
              initialBalance: 0,
              netBalance: 0,
            };
          }
        }
      } catch (error) {
        console.error("Error loading bank account statement:", error);
        Swal.fire({
          icon: "error",
          title: this.$tn("Error"),
          text: this.$tn("Failed to load bank account statement"),
          footer: error.message,
        });
        if (!append) {
          this.transactions = [];
          this.pagination = {};
          this.bankAccountCurrency = "";
        }
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },

    async loadMore() {
      if (this.pagination.hasNextPage && !this.loadingMore) {
        this.currentPage += 1;
        await this.loadBankAccountStatement(true);
      }
    },

    getTransactionTypeLabel(type) {
      const typeLabels = {
        payment: this.$t("Payment"),
        collect: this.$t("Collection"),
      };
      return typeLabels[type] || type;
    },

    getTransactionTypeBadgeClass(type) {
      const badgeClasses = {
        payment: "bg-danger",
        collect: "bg-success",
      };
      return badgeClasses[type] || "bg-secondary";
    },

    getAmountClass(type) {
      const amountClasses = {
        payment: "text-danger",
        collect: "text-success",
      };
      return amountClasses[type] || "text-muted";
    },

    getBalanceClass(balance) {
      if (balance > 0) return "text-success";
      if (balance < 0) return "text-danger";
      return "text-muted";
    },

    /** Balance including initial balance so the column reflects full calculation */
    getDisplayBalance(transaction) {
      const running = Number(transaction.runningBalance) || 0;
      const initial = Number(this.initialBalance) || 0;
      return running + initial;
    },

    formatAmount(amount, currency = "USD") {
      if (!amount) return `${currency || "USD"} 0.00`;
      // Siempre mostrar decimales para valores monetarios
      const formatted = formatNumberSync(amount, null, true);
      return `${currency || "USD"} ${formatted}`;
    },

    formatAmountWithCurrency(amount, currency = "USD") {
      if (!amount) return `0.00 ${currency || "USD"}`;
      // Siempre mostrar decimales para valores monetarios
      const formatted = formatNumberSync(amount, null, true);
      return `${formatted} ${currency || "USD"}`;
    },

    formatBalanceWithSign(balance, currency = "USD") {
      if (balance === null || balance === undefined || isNaN(balance)) {
        return `0.00 ${currency || "USD"}`;
      }
      const numBalance = Number(balance);
      if (isNaN(numBalance)) {
        return `0.00 ${currency || "USD"}`;
      }
      // Siempre mostrar decimales para valores monetarios
      const formatted = formatNumberSync(Math.abs(numBalance), null, true);
      const sign = numBalance >= 0 ? "+" : "-";
      return `${sign}${formatted} ${currency || "USD"}`;
    },

    /** Table columns: amount without currency (currency shown above) */
    formatAmountOnly(amount) {
      if (amount === null || amount === undefined || isNaN(Number(amount))) return "0.00";
      return formatNumberSync(amount, null, true);
    },

    /** Table columns: balance with sign, without currency (currency shown above) */
    formatBalanceWithSignOnly(balance) {
      if (balance === null || balance === undefined || isNaN(balance)) return "0.00";
      const numBalance = Number(balance);
      if (isNaN(numBalance)) return "0.00";
      const formatted = formatNumberSync(Math.abs(numBalance), null, true);
      const sign = numBalance >= 0 ? "+" : "-";
      return `${sign}${formatted}`;
    },

    formatDate(date) {
      return formatDateSync(date, this.$t("-"));
    },

    formatStatementTransitDays(row) {
      const raw = row && row.shippingTransitDays;
      if (raw == null || raw === "") return "—";
      const n = Number(raw);
      return Number.isFinite(n) ? String(n) : "—";
    },

    truncateText(text, maxLength) {
      if (!text) return this.$t("");
      return text.length > maxLength
        ? text.substring(0, maxLength) + "..."
        : text;
    },

    /** Etiqueta de una columna para la cabecera del Excel. */
    exportColumnLabel(columnKey) {
      const col = BANK_STATEMENT_COLUMNS.find((c) => c.key === columnKey);
      return (col && col.label) || columnKey;
    },

    async exportToExcel() {
      if (!this.bankAccountId) return;

      try {
        this.exportingExcel = true;
        Swal.fire({
          title: this.$tn("Export to Excel"),
          text: this.$tn("Loading..."),
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const response = await wencesApi.get(
          `/api/financial/bank-account-statement/${this.bankAccountId}`,
          {
            params: {
              page: 1,
              limit: 100000,
              sortBy: "date",
              sortOrder: "desc",
              startDate: this.startDate || "2026-03-01",
              endDate: this.endDate || undefined,
              hideEmptyDate: this.hideEmptyDate ? true : undefined,
              ...this.statementQueryParams(),
            },
          }
        );

        if (!response.data?.success || !response.data?.data) {
          throw new Error(response.data?.error || "Failed to load data for export");
        }

        const {
          transactions: allTransactions,
          initialBalance,
          totals: exportTotals,
          bankAccountName,
          bankAccountCurrency: exportBankCurrency,
        } = response.data.data;
        const summaryCurrency =
          exportBankCurrency ||
          this.bankAccountCurrency ||
          (allTransactions || []).find(
            (t) =>
              t &&
              (t.type === "payment" || t.type === "collect") &&
              t.currency
          )?.currency ||
          "USD";

        const data = [];

        // El Excel sigue las columnas visibles y su orden, como se ve en pantalla.
        const exportColumns = this.displayTableColumns.map((col) => col.key);

        data.push(exportColumns.map((key) => this.$t(this.exportColumnLabel(key))));

        if (
          initialBalance !== 0 &&
          initialBalance !== null &&
          initialBalance !== undefined
        ) {
          data.push(
            exportColumns.map((key) => {
              if (key === "type") return this.$t("Initial Balance");
              if (key === "balance") {
                return this.formatBalanceWithSign(initialBalance, summaryCurrency);
              }
              if (key === "date") return "";
              return "-";
            })
          );
        }

        (allTransactions || []).forEach((transaction) => {
          const debit =
            transaction.type === "payment"
              ? this.formatAmountWithCurrency(
                  transaction.amount,
                  transaction.currency
                )
              : "-";
          const credit =
            transaction.type === "collect"
              ? this.formatAmountWithCurrency(
                  transaction.amount,
                  transaction.currency
                )
              : "-";
          const transitCell = this.formatStatementTransitDays(transaction);
          const cellByKey = {
            date: this.formatDate(transaction.date),
            type: this.getTransactionTypeLabel(transaction.type),
            description: transaction.description || "-",
            transitDays: transitCell === "—" ? "-" : transitCell,
            contact: transaction.contact || "-",
            debit,
            credit,
            balance: this.formatBalanceWithSign(
              this.getDisplayBalance(transaction),
              transaction.currency
            ),
            comment: transaction.comment || "-",
            attachedFile: transaction.attachedFile ? this.$t("Yes") : "-",
          };
          data.push(exportColumns.map((key) => cellByKey[key] ?? "-"));
        });

        data.push([]);
        const totalsByKey = {
          date: this.$t("TOTALS"),
          debit: this.formatAmountWithCurrency(
            exportTotals?.totalPayments || 0,
            summaryCurrency
          ),
          credit: this.formatAmountWithCurrency(
            exportTotals?.totalCollects || 0,
            summaryCurrency
          ),
          balance: this.formatBalanceWithSign(
            exportTotals?.netBalance || 0,
            summaryCurrency
          ),
        };
        // Sin la columna de fecha, el rótulo TOTALS va en la primera visible.
        if (!exportColumns.includes("date") && exportColumns.length) {
          totalsByKey[exportColumns[0]] =
            totalsByKey[exportColumns[0]] || this.$t("TOTALS");
        }
        data.push(exportColumns.map((key) => totalsByKey[key] || ""));

        const safeName = (bankAccountName || "statement")
          .replace(/[^a-zA-Z0-9-_]/g, "_")
          .substring(0, 50);
        const fileName = `bank_account_statement_${safeName}_${new Date().toISOString().split("T")[0]}`;

        Swal.close();
        await ExportService.exportDataToExcel(
          data,
          fileName,
          this.$t("Bank Account Statement")
        );

        Swal.fire({
          icon: "success",
          title: this.$tn("Export Successful"),
          text: this.$tn("Data exported successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error exporting bank account statement to Excel:", error);
        Swal.close();
        Swal.fire({
          icon: "error",
          title: this.$tn("Export Error"),
          text:
            error.response?.data?.error ||
            error.message ||
            this.$tn("Failed to export data"),
        });
      } finally {
        this.exportingExcel = false;
      }
    },

    viewTransaction(transaction) {
      if (transaction.type === "payment") {
        // Rows from payment_appliedto use composite _id (paymentId_appliedToId); navigate by real payment id
        if (
          transaction.isBankCommission &&
          transaction.collectId &&
          !transaction.paymentId
        ) {
          this.$router.push(
            `/dynamic-model/collect/view/${transaction.collectId}`
          );
          return;
        }
        const paymentId =
          transaction.paymentId ||
          (typeof transaction._id === "string" &&
          /^[a-f0-9]{24}_[a-f0-9]{24}(?:_bank_commission)?$/i.test(
            transaction._id
          )
            ? transaction._id.split("_")[0]
            : transaction._id);
        this.$router.push(`/dynamic-model/payment/view/${paymentId}`);
      } else if (transaction.type === "collect") {
        const collectId = transaction.collectId || transaction._id;
        this.$router.push(`/dynamic-model/collect/view/${collectId}`);
      }
    },

    viewContact(contactId) {
      if (contactId) {
        this.$router.push(`/dynamic-model/Contact/view/${contactId}`);
      } else {
        Swal.fire({
          icon: "warning",
          title: this.$tn("Warning"),
          text: this.$tn("Contact ID not available for navigation"),
          footer: this.$tn("Contact ID not available for navigation"),
        });
      }
    },

    async viewAttachedFile(file) {
      if (!file) return;

      // Si ya es una URL completa (http/https), abrir directamente
      if (typeof file === "string" && (file.startsWith("http://") || file.startsWith("https://"))) {
        window.open(file, "_blank");
        return;
      }

      // Si es una ruta local como /dynamic-model/BankAccount/view/fileuploads/..., extraer el fileKey
      if (typeof file === "string") {
        let fileKey = file;
        
        // Extraer fileKey de rutas locales
        if (file.includes("/fileuploads/")) {
          const parts = file.split("/fileuploads/");
          if (parts.length > 1) {
            fileKey = parts[1];
          }
        } else if (file.startsWith("fileuploads/")) {
          fileKey = file.replace("fileuploads/", "");
        }
        
        // Obtener URL presignada de S3
        try {
          const response = await wencesApi.get(
            `/api/upload/file/${fileKey}`
          );
          if (response.data && response.data.url) {
            window.open(response.data.url, "_blank");
          } else {
            Swal.fire({
              icon: "error",
              title: this.$tn("Error"),
              text: this.$tn("Error accessing file"),
            });
          }
        } catch (error) {
          console.error("Error getting presigned URL for file:", error);
          Swal.fire({
            icon: "error",
            title: this.$tn("Error"),
            text: this.$tn("Error accessing file"),
          });
        }
      } else if (file?.url) {
        window.open(file.url, "_blank");
      }
    },
  },
};
</script>

<style scoped>
/* ---- Anchos de columna. Espejo de BANK_STATEMENT_COLUMN_WIDTH_PX: el mixin
   usa esos px para el offset de las columnas fijadas. ---- */
.bank-statement-table .bas-col-date {
  width: 105px;
}

.bank-statement-table .bas-col-type {
  width: 130px;
}

.bank-statement-table .bas-col-description {
  width: 220px;
}

.bank-statement-table .bas-col-transit {
  width: 90px;
}

.bank-statement-table .bas-col-contact {
  width: 170px;
}

.bank-statement-table .bas-col-money {
  width: 115px;
}

.bank-statement-table .bas-col-comment {
  width: 165px;
}

.bank-statement-table .bas-col-file {
  width: 115px;
}

.bank-statement-table .bas-col-actions {
  width: 110px;
}

/* Cabecera arrastrable para reordenar columnas. */
.bank-statement-table th.bas-th-draggable {
  position: relative;
  cursor: grab;
  user-select: none;
  padding-right: 14px;
}

.bank-statement-table th.bas-th-draggable:active {
  cursor: grabbing;
}

.bank-statement-table th.bas-th-draggable:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.bank-statement-table th.bas-th-draggable.dragging {
  opacity: 0.5;
}

.bank-statement-table th.bas-th-draggable.drag-over {
  border-left: 2px solid #0d6efd;
}

.bank-statement-table th.bas-th-draggable button {
  cursor: pointer;
}

/* Icono de ordenar: hereda el color de la cabecera y juega con la opacidad. */
.bank-statement-table th .column-header-sort-icon {
  flex-shrink: 0;
  font-size: 1em;
  line-height: 1;
  padding: 2px 3px;
  border-radius: 3px;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

.bank-statement-table th.bas-th-draggable:hover .column-header-sort-icon {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.08);
}

.bank-statement-table th .column-header-sort-icon:hover {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.18);
}

.bank-statement-table th.sorted-asc .column-header-sort-icon,
.bank-statement-table th.sorted-desc .column-header-sort-icon {
  opacity: 1;
}

.bank-statement-table th.sorted-asc,
.bank-statement-table th.sorted-desc {
  background-color: rgba(13, 110, 253, 0.08);
}

/* Tirador de ancho, en el borde derecho de cada cabecera. */
.bas-resize-handle {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  border-right: 2px solid transparent;
  transition: border-color 0.15s ease;
  z-index: 20;
  touch-action: none;
}

.bank-statement-table th.bas-th-draggable:hover .bas-resize-handle {
  border-right-color: rgba(13, 110, 253, 0.35);
}

.bas-resize-handle:hover {
  border-right-color: #0d6efd;
}

/* El tema deja .table-responsive sin scroll en escritorio y la sección de la
   ficha recorta lo que sobra: la tabla scrollea sola para llegar a las
   columnas de la derecha (y la fijada queda quieta al scrollear). */
.bas-table-scroll {
  overflow-x: auto;
}

/* ---- Columna fijada ----
   Una celda es transparente por defecto: sin fondo propio, las columnas que
   scrollean se ven por debajo de la fijada. El color sale del <tr> con
   --bas-row-bg, así respeta hover y la fila de saldo inicial. */
.bank-statement-table thead tr,
.bank-statement-table tbody tr {
  --bas-row-bg: #fff;
}

.bank-statement-table.table-hover > tbody > tr:hover {
  --bas-row-bg: #ececec;
}

/* Saldo inicial: bg-info + bg-opacity-10 aplanado sobre blanco. */
.bank-statement-table tbody tr.bg-info {
  --bas-row-bg: #e7f8fd;
}

.bank-statement-table thead th.list-col-pinned,
.bank-statement-table tbody td.list-col-pinned {
  background-color: var(--bas-row-bg, #fff) !important;
  --bs-table-bg: var(--bas-row-bg, #fff) !important;
  --bs-table-accent-bg: var(--bas-row-bg, #fff) !important;
  background-clip: padding-box;
}

/* Última columna fijada: marca el corte con el área que scrollea. */
.bank-statement-table .list-col-pinned-boundary {
  box-shadow: 3px 0 6px -2px rgba(0, 0, 0, 0.12);
}

.bank-account-statement .table tbody tr {
  transition: all 0.2s ease;
}

.bank-account-statement .table tbody tr:hover {
  background-color: #f8f9fa;
}

.cursor-pointer {
  cursor: pointer;
}

.text-danger {
  color: #dc3545 !important;
}

.text-success {
  color: #198754 !important;
}

.bg-danger {
  background-color: #dc3545 !important;
}

.bg-success {
  background-color: #198754 !important;
}
</style>
